import axios from "axios";
import { jwtDecode, type JwtPayload } from "jwt-decode";

import { identityBaseUrl } from "shared/constants";
import {
  cachedRefreshToken,
  currentRefreshToken,
  currentToken,
  getActiveOrganisationId,
  isAdminUser,
  updateUserRefreshToken,
  updateUserToken,
} from "shared/helpers/authorization";
import store from "shared/services/localForageInstance";
import StorageService from "shared/services/StorageService";
import type { AccountManager } from "shared/stores/user/types";

const REFRESH_LOCK_WINDOW_MS = 15_000;
const REFRESH_LOCK_POLL_INTERVAL_MS = 100;
const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000;

let inFlightRefreshPromise: Promise<string> | null = null;

// Ensure only one request to the auth service is made at a time per tab,
// while coordinating refresh calls across tabs with a shared timestamp lock.
function handleTokenRefresh(): Promise<string> {
  // Collapse concurrent refresh calls in the same tab onto one promise.
  if (inFlightRefreshPromise) {
    return inFlightRefreshPromise;
  }

  inFlightRefreshPromise = (async () => {
    const isAdmin = await isAdminUser();

    const organisationId = getActiveOrganisationId();

    const refreshLockKey = `tokenRefreshStartedAt:${organisationId}${isAdmin ? ":admin" : ""}`;

    const lockValue = await store.getItem<string>(refreshLockKey);
    const lockStartedAt = Number(lockValue || 0);
    const lockIsActive = Date.now() - lockStartedAt <= REFRESH_LOCK_WINDOW_MS;

    if (lockIsActive) {
      // Another tab refreshed recently; wait briefly for lock release and then try shared token.
      const lockDeadline = Date.now() + REFRESH_LOCK_WINDOW_MS;

      const waitForLockRelease = async (): Promise<void> => {
        if (Date.now() >= lockDeadline) {
          return;
        }

        const latestLockValue = await store.getItem<string>(refreshLockKey);
        const latestLockStartedAt = Number(latestLockValue || 0);

        const latestLockIsActive =
          Date.now() - latestLockStartedAt <= REFRESH_LOCK_WINDOW_MS;

        if (!latestLockIsActive) {
          return;
        }

        await new Promise<void>((resolve) => {
          setTimeout(resolve, REFRESH_LOCK_POLL_INTERVAL_MS);
        });

        await waitForLockRelease();
      };

      await waitForLockRelease();

      const sharedTokenAfterWait = await currentToken();

      if (sharedTokenAfterWait) {
        try {
          // Require the same refresh buffer used by ensureTokenValidity to avoid refresh churn.
          if (
            getTokenExpiration(sharedTokenAfterWait) >
            Date.now() + REFRESH_BEFORE_EXPIRY_MS
          ) {
            return sharedTokenAfterWait;
          }
        } catch {
          // Ignore malformed shared token and continue with refresh flow.
        }
      }
    }

    const organisationRefreshToken = await cachedRefreshToken(organisationId);

    const refreshTokenToUse =
      organisationRefreshToken || (await currentRefreshToken());

    if (!refreshTokenToUse) {
      return "";
    }

    await StorageService.set(refreshLockKey, String(Date.now()), store);

    try {
      const response = await axios.post(`${identityBaseUrl}/oauth/token`, {
        client_id: "identity-service",
        grant_type: "refresh_token",
        refresh_token: refreshTokenToUse,
      });

      const newToken = response.data.access_token;
      const newRefreshToken = response.data.refresh_token;

      await updateUserToken(newToken, organisationId);
      await updateUserRefreshToken(newRefreshToken, organisationId);

      return newToken;
    } catch {
      return "";
    } finally {
      await StorageService.set(refreshLockKey, "0", store);
    }
  })().finally(() => {
    inFlightRefreshPromise = null;
  });

  return inFlightRefreshPromise;
}

// We are not following the standard
interface CustomJwtPayload extends JwtPayload {
  expires?: number;
}

function getTokenExpiration(token: string): number {
  const decoded: CustomJwtPayload = jwtDecode(token);

  return (decoded.expires || 0) * 1000; // exp is in seconds, convert to ms
}

/**
 * Returns a valid access token for request interceptors.
 *
 * Flow:
 * 1) Read the organisation-scoped shared token using session organisation context.
 * 2) If it is still valid for >5m, return it.
 * 3) If near expiry, try a newer shared token from localForage.
 * 4) If expired or within the refresh buffer, refresh immediately.
 */
export default async function ensureTokenValidity() {
  const sessionToken = (await currentToken()) || "";

  const currentTime = new Date().getTime();
  let expirationTime = 0;

  if (sessionToken) {
    try {
      expirationTime = getTokenExpiration(sessionToken);

      if (expirationTime > currentTime + REFRESH_BEFORE_EXPIRY_MS) {
        return sessionToken;
      }
    } catch {
      expirationTime = 0;
    }
  }

  // New tabs can start with empty sessionStorage; block to avoid returning an empty bearer.
  // If this tab has no local token, block on refresh instead of returning an empty token.
  if (!sessionToken) {
    return handleTokenRefresh();
  }

  // Synchronously refresh the token if it's expired or within the refresh window.
  if (expirationTime <= currentTime + REFRESH_BEFORE_EXPIRY_MS) {
    return handleTokenRefresh();
  }

  // Asynchronously refresh the token if it's 5 mins from expiration
  if (expirationTime < currentTime + REFRESH_BEFORE_EXPIRY_MS) {
    handleTokenRefresh();
  }

  return sessionToken;
}

interface SignInTokenResponse {
  access_token: string;
  enforce_two_factor?: boolean;
  refresh_token: string;
  weak_pin: false;
}

interface WeakPinSignInResponse {
  weak_pin: true;
}

type SignInResponse = SignInTokenResponse | WeakPinSignInResponse;

export function signIn(
  emailOrPhone: string,
  pin: string,
  platform?: string,
  twoFactorCode?: string
): Promise<SignInResponse> {
  return axios
    .post(`${identityBaseUrl}/login`, {
      password: pin,
      platform,
      two_factor_code: twoFactorCode,
      username: emailOrPhone,
    })
    .then((response) => response.data);
}

export async function exchangeAuthHandoff(code: string): Promise<{
  access_token: string;
  account_manager: AccountManager;
  refresh_token: string;
}> {
  const response = await axios.post(`${identityBaseUrl}/auth/exchange`, {
    code,
  });

  return response.data;
}

export async function revokeToken(refreshToken: string): Promise<void> {
  await axios.post(`${identityBaseUrl}/oauth/revoke`, {
    client_id: "identity-service",
    token: refreshToken,
    token_type_hint: "refresh_token",
  });
}
